import { Alert, Box, Grid, Stack, SxProps } from '@mui/material';
import { t } from 'i18next';
import JiraIcon from '../../../assets/images/export-jira-icon.svg';
import TaniumIcon from '../../../assets/images/tanium-icon.svg';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { Interpolation } from '../../../config/i18n/components/Interpolation';
import { PATHS } from '../../../routing/routes';
import { IntegrationAccordion } from '../../../common/components/IntegrationAccordion';
import { useState } from 'react';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { ProjectTanium as ProjectTaniumModel } from '../../../integrations/tanium/models/ProjectTanium';
import { ProjectTaniumSession } from './ProjectTaniumSession';
import { ProjectTaniumConfigForm } from './ProjectTaniumConfigForm';
import { ProjectJiraForm } from './ProjectJiraForm';

type Props = {
  initialLoad: boolean;
  isLoading: boolean;
  jira: {
    canUpdateJira: boolean;
    issueExport: {
      isJiraLinkEstablished: boolean;
      isIssueExportConfigured: boolean;
      baseUrl: string;
      projectKey: string;
      issueType: string;
      onUnlinkIssueExport: () => void;
      onTestIssueExport: (projectKey: string, issueType: string) => void;
    };
    issueCollector: {
      isIssueCollectorConfigured: boolean;
      formCode: string;
      onUnlinkIssueCollector: () => void;
    };
    handleSave: (formCode: string, projectKey: string, issueType: string) => void;
  };
  tanium: {
    isOrgIntegrationConfigured: boolean;
    isTaniumActive: boolean;
    projectTanium: ProjectTaniumModel | null;
    canUpdateTanium: boolean;
    handleSave: (computerGroup: string) => void;
    handleTestComputerGroup: (computerGroup: string) => void;
    handleStartTanium: () => void;
    handleStopTanium: () => void;
    handleUnlinkTanium: () => void;
    handleGetLog: () => void;
  };
};

export const STYLES = {
  headerTitle: { textTransform: 'uppercase', color: 'text.secondary', fontWeight: 500 },
  integrationsContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
} satisfies Record<string, SxProps>;

export const enum PANELS {
  JIRA = 'jira',
  TANIUM = 'tanium',
}

export const ProjectIntegrations = ({ initialLoad, isLoading, jira, tanium }: Props) => {
  const [expanded, setExpanded] = useState<PANELS | false>(false);

  const handleChange = (panel: PANELS) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const isJiraActive = (jira.issueExport.isJiraLinkEstablished && jira.issueExport.isIssueExportConfigured) || jira.issueCollector.isIssueCollectorConfigured;
  const showTanium = getFeatureFlag<boolean>(FeatureFlagsType.ENABLE_TANIUM_INTEGRATION);

  return (
    <Box sx={STYLES.integrationsContent}>
      <Alert severity="info" sx={{ width: 1, borderRadius: 0, textAlign: 'center' }}>
        <Interpolation
          i18nKey="projectIntegrations.projectSettingsNote"
          variant="body2"
          items={[{ name: 'anchor', href: UrlHelper.generatePath(PATHS.INTEGRATIONS) }]}
        />
      </Alert>
      <Stack spacing={2}>
        <IntegrationAccordion
          icon={<JiraIcon height="31" width="31" />}
          title={t('projectIntegrations.integrationsList.jiraTitle')}
          subtitle={t('integrationList.jiraDescription')}
          isConfigured={isJiraActive}
          isLoading={initialLoad}
          isExpanded={expanded === PANELS.JIRA}
          onClick={handleChange(PANELS.JIRA)}
          details={
            <ComponentLoader loading={isLoading}>
              <ProjectJiraForm
                isLoading={isLoading}
                canEdit={jira.canUpdateJira}
                issueCollectorCode={jira.issueCollector.formCode}
                onIssueCollectorUnlink={jira.issueCollector.onUnlinkIssueCollector}
                handleSave={jira.handleSave}
                isJiraLinkEstablished={jira.issueExport.isJiraLinkEstablished}
                isIssueExportConfigured={jira.issueExport.isIssueExportConfigured}
                jiraBaseUrl={jira.issueExport.baseUrl}
                jiraProjectKey={jira.issueExport.projectKey}
                jiraIssueType={jira.issueExport.issueType}
                onIssueExportUnlink={jira.issueExport.onUnlinkIssueExport}
                onTestIssueExport={jira.issueExport.onTestIssueExport}
              />
            </ComponentLoader>
          }
        />
        {showTanium && (
          <IntegrationAccordion
            icon={<TaniumIcon width="32" height="32" />}
            title={t('projectIntegrations.integrationsList.taniumTitle')}
            subtitle={t('integrationList.tanium.description')}
            isConfigured={tanium.isTaniumActive}
            isLoading={initialLoad}
            isExpanded={expanded === PANELS.TANIUM}
            onClick={handleChange(PANELS.TANIUM)}
            details={
              <ComponentLoader loading={isLoading}>
                {!tanium.canUpdateTanium && <Alert severity="info">{t('projectIntegrations.noPermission')}</Alert>}
                <Grid container sx={{ minHeight: '22rem' }}>
                  <Grid item xs={6}>
                    <ProjectTaniumConfigForm
                      isLoading={isLoading}
                      isOrgIntegrationConfigured={tanium.isOrgIntegrationConfigured}
                      projectTanium={tanium.projectTanium}
                      canUpdate={tanium.canUpdateTanium}
                      handleSave={tanium.handleSave}
                      handleTestComputerGroup={tanium.handleTestComputerGroup}
                      handleUnlinkTanium={tanium.handleUnlinkTanium}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ProjectTaniumSession
                      isLoading={isLoading}
                      isOrgIntegrationConfigured={tanium.isOrgIntegrationConfigured}
                      projectTanium={tanium.projectTanium}
                      canUpdate={tanium.canUpdateTanium}
                      handleStartTanium={tanium.handleStartTanium}
                      handleStopTanium={tanium.handleStopTanium}
                      handleGetLog={tanium.handleGetLog}
                    />
                  </Grid>
                </Grid>
              </ComponentLoader>
            }
          />
        )}
      </Stack>
    </Box>
  );
};
